import styled from 'styled-components'
import { omniaTheme } from '../../theme/omniaTheme'

// ============================================================
// Page-level wrapper (gray bg — NOT purple — per Omnia brand)
// ============================================================

export const BrokerPage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 14mm 14mm 14mm 14mm;
  box-sizing: border-box;

  @media print {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    padding: 14mm 14mm 14mm 14mm !important;
  }
`

// ============================================================
// Header — title + page number + 64px underline
// ============================================================

export const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;

  @media print {
    display: flex !important;
    align-items: baseline !important;
    justify-content: space-between !important;
  }
`

export const PageTitle = styled.h1`
  font-family: ${omniaTheme.fonts.heading};
  font-size: 32px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: ${omniaTheme.colors.black};
  letter-spacing: ${omniaTheme.letterSpacing.tight};
  line-height: 1.05;
  margin: 0;

  @media print {
    font-family: ${omniaTheme.fonts.heading} !important;
    font-size: 32px !important;
    font-weight: ${omniaTheme.fontWeights.light} !important;
    color: ${omniaTheme.colors.black} !important;
    letter-spacing: ${omniaTheme.letterSpacing.tight} !important;
    line-height: 1.05 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const PageNumber = styled.span`
  font-family: ${omniaTheme.fonts.label};
  font-size: 11px;
  font-weight: ${omniaTheme.fontWeights.light};
  letter-spacing: ${omniaTheme.letterSpacing.wide};
  color: ${omniaTheme.colors.textLabel};
  text-transform: uppercase;

  @media print {
    font-family: ${omniaTheme.fonts.label} !important;
    font-size: 11px !important;
    color: ${omniaTheme.colors.textLabel} !important;
    text-transform: uppercase !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const TitleUnderline = styled.div`
  width: 100%;
  height: 0.5px;
  background: ${omniaTheme.colors.strokeLine};
  margin: 12px 0 32px 0;

  @media print {
    width: 100% !important;
    height: 0.5px !important;
    background: ${omniaTheme.colors.strokeLine} !important;
    margin: 12px 0 32px 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// ============================================================
// Body — two-column layout
// ============================================================

export const Body = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 24px;
  flex: 1;

  @media print {
    display: grid !important;
    grid-template-columns: 180px 1fr !important;
    gap: 24px !important;
    flex: 1 !important;
  }
`

// ============================================================
// LEFT column — photo + name + contacts
// ============================================================

export const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;

  @media print {
    display: flex !important;
    flex-direction: column !important;
  }
`

export const BrokerPhoto = styled.div<{ $imageUrl?: string }>`
  width: 180px;
  height: 220px;
  background: ${({ $imageUrl }) =>
    $imageUrl ? `url('${$imageUrl}') center/cover no-repeat` : '#E5E5E5'};
  border-radius: 0;
  flex-shrink: 0;

  @media print {
    width: 180px !important;
    height: 220px !important;
    background: ${({ $imageUrl }) =>
      $imageUrl
        ? `url('${$imageUrl}') center/cover no-repeat !important`
        : '#E5E5E5 !important'};
    border-radius: 0 !important;
    flex-shrink: 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const BrokerName = styled.div`
  font-family: ${omniaTheme.fonts.heading};
  font-size: 18px;
  font-weight: ${omniaTheme.fontWeights.medium};
  color: ${omniaTheme.colors.black};
  text-align: center;
  letter-spacing: ${omniaTheme.letterSpacing.tight};
  margin-top: 32px;

  @media print {
    font-family: ${omniaTheme.fonts.heading} !important;
    font-size: 18px !important;
    font-weight: ${omniaTheme.fontWeights.medium} !important;
    color: ${omniaTheme.colors.black} !important;
    text-align: center !important;
    margin-top: 32px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const BrokerTitle = styled.div`
  font-family: ${omniaTheme.fonts.body};
  font-size: 11px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: ${omniaTheme.colors.textMuted};
  text-align: center;
  margin-top: 4px;

  @media print {
    font-family: ${omniaTheme.fonts.body} !important;
    font-size: 11px !important;
    color: ${omniaTheme.colors.textMuted} !important;
    text-align: center !important;
    margin-top: 4px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;

  @media print {
    display: flex !important;
    flex-direction: column !important;
    margin-top: 32px !important;
  }
`

export const ContactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 0.5px solid ${omniaTheme.colors.border};

  &:last-child {
    border-bottom: 0;
  }

  @media print {
    display: flex !important;
    align-items: center !important;
    gap: 12px !important;
    padding: 10px 0 !important;
    border-bottom: 0.5px solid ${omniaTheme.colors.border} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;

    &:last-child {
      border-bottom: 0 !important;
    }
  }
`

export const ContactIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${omniaTheme.colors.primary};

  svg {
    width: 16px;
    height: 16px;
    display: block;
  }

  @media print {
    width: 16px !important;
    height: 16px !important;
    display: flex !important;
    color: ${omniaTheme.colors.primary} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const ContactText = styled.span`
  font-family: ${omniaTheme.fonts.body};
  font-size: 11px;
  font-weight: ${omniaTheme.fontWeights.normal};
  color: ${omniaTheme.colors.text};
  word-break: break-all;

  @media print {
    font-family: ${omniaTheme.fonts.body} !important;
    font-size: 11px !important;
    color: ${omniaTheme.colors.text} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// ============================================================
// RIGHT column — 3 stacked cards
// ============================================================

export const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media print {
    display: flex !important;
    flex-direction: column !important;
    gap: 16px !important;
  }
`

export const Card = styled.div`
  background: ${omniaTheme.colors.cardBg};
  padding: 24px;
  border: 0;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media print {
    background: ${omniaTheme.colors.cardBg} !important;
    padding: 24px !important;
    border: 0 !important;
    border-radius: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 12px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const CardTitle = styled.h3`
  font-family: ${omniaTheme.fonts.heading};
  font-size: 14px;
  font-weight: ${omniaTheme.fontWeights.medium};
  color: ${omniaTheme.colors.black};
  letter-spacing: ${omniaTheme.letterSpacing.tight};
  line-height: 1.2;
  margin: 0;

  @media print {
    font-family: ${omniaTheme.fonts.heading} !important;
    font-size: 14px !important;
    font-weight: ${omniaTheme.fontWeights.medium} !important;
    color: ${omniaTheme.colors.black} !important;
    line-height: 1.2 !important;
    margin: 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const CardParagraph = styled.p`
  font-family: ${omniaTheme.fonts.body};
  font-size: 11px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: ${omniaTheme.colors.text};
  line-height: 1.5;
  margin: 0;

  @media print {
    font-family: ${omniaTheme.fonts.body} !important;
    font-size: 11px !important;
    color: ${omniaTheme.colors.text} !important;
    line-height: 1.5 !important;
    margin: 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// Domaines d'expertise — chip row inside the right-column card
export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 4px;

  @media print {
    display: flex !important;
    flex-wrap: wrap !important;
    gap: 12px !important;
    margin-top: 4px !important;
  }
`

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 16px;
  background: ${omniaTheme.colors.cardBg};
  border: 0.5px solid ${omniaTheme.colors.border};
  font-family: ${omniaTheme.fonts.body};
  font-size: 12px;
  font-weight: ${omniaTheme.fontWeights.medium};
  color: ${omniaTheme.colors.primary};
  white-space: nowrap;

  @media print {
    display: inline-flex !important;
    align-items: center !important;
    height: 32px !important;
    padding: 0 16px !important;
    background: ${omniaTheme.colors.cardBg} !important;
    border: 0.5px solid ${omniaTheme.colors.border} !important;
    font-family: ${omniaTheme.fonts.body} !important;
    font-size: 12px !important;
    font-weight: ${omniaTheme.fontWeights.medium} !important;
    color: ${omniaTheme.colors.primary} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// Agence card — UPPERCASE Roboto Condensed label above an address block
export const CardLabel = styled.div`
  font-family: ${omniaTheme.fonts.label};
  font-size: 9px;
  font-weight: ${omniaTheme.fontWeights.light};
  letter-spacing: ${omniaTheme.letterSpacing.wide};
  color: ${omniaTheme.colors.textLabel};
  text-transform: uppercase;
  margin-top: 4px;

  @media print {
    font-family: ${omniaTheme.fonts.label} !important;
    font-size: 9px !important;
    color: ${omniaTheme.colors.textLabel} !important;
    letter-spacing: ${omniaTheme.letterSpacing.wide} !important;
    text-transform: uppercase !important;
    margin-top: 4px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const CardAddress = styled.p`
  font-family: ${omniaTheme.fonts.body};
  font-size: 11px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: ${omniaTheme.colors.text};
  line-height: 1.5;
  margin: 0;
  /* Honor explicit newlines from the mapper (street \n postal city) */
  white-space: pre-line;

  & + & {
    /* Vertical breathing room between the address block and the
       phone/website line below it. */
    margin-top: 14px;
  }

  strong {
    font-weight: ${omniaTheme.fontWeights.medium};
    color: ${omniaTheme.colors.black};
  }

  @media print {
    font-family: ${omniaTheme.fonts.body} !important;
    font-size: 11px !important;
    color: ${omniaTheme.colors.text} !important;
    line-height: 1.5 !important;
    margin: 0 !important;
    white-space: pre-line !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;

    & + & {
      margin-top: 14px !important;
    }
  }
`

// ============================================================
// Bottom row — divider + logo + agency address
// ============================================================

export const BottomDivider = styled.hr`
  width: 100%;
  height: 0;
  border: 0;
  border-top: 0.5px solid ${omniaTheme.colors.border};
  margin: 32px 0 16px 0;

  @media print {
    border-top: 0.5px solid ${omniaTheme.colors.border} !important;
    margin: 32px 0 16px 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const BottomRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  @media print {
    display: flex !important;
    justify-content: space-between !important;
    align-items: flex-end !important;
  }
`

export const BottomLogoSlot = styled.div`
  display: flex;
  align-items: flex-end;

  @media print {
    display: flex !important;
    align-items: flex-end !important;
  }
`

export const AgencyInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;

  @media print {
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-end !important;
    gap: 4px !important;
  }
`

export const AgencyName = styled.div`
  font-family: ${omniaTheme.fonts.heading};
  font-size: 11px;
  font-weight: ${omniaTheme.fontWeights.medium};
  color: ${omniaTheme.colors.black};

  @media print {
    font-family: ${omniaTheme.fonts.heading} !important;
    font-size: 11px !important;
    font-weight: ${omniaTheme.fontWeights.medium} !important;
    color: ${omniaTheme.colors.black} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const AgencyAddress = styled.div`
  font-family: ${omniaTheme.fonts.body};
  font-size: 10px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: ${omniaTheme.colors.textMuted};

  @media print {
    font-family: ${omniaTheme.fonts.body} !important;
    font-size: 10px !important;
    color: ${omniaTheme.colors.textMuted} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`
